<template>
  <div class="profile-dropdown" ref="dropdown">
    <!-- Profile Button -->
    <button
      class="profile-button"
      @click="toggleDropdown"
      :class="{ active: isOpen, collapsed: collapsed }"
      aria-label="User profile menu"
    >
      <div class="profile-avatar">
        <img
          v-if="userProfileImage"
          :src="userProfileImage"
          alt="Profile"
          class="avatar-img"
        />
        <div v-else class="avatar-placeholder">
          {{ userInitials }}
        </div>
      </div>
      <span v-if="!collapsed" class="profile-name">{{ userFullName }}</span>
      <svg
        v-if="!collapsed"
        class="dropdown-arrow"
        :class="{ rotated: isOpen }"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M7 10l5 5 5-5z"/>
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <transition name="dropdown">
      <div v-if="isOpen" class="dropdown-menu">
        <!-- User Info Header -->
        <div class="dropdown-header">
          <div class="header-avatar">
            <img
              v-if="userProfileImage"
              :src="userProfileImage"
              alt="Profile"
              class="header-avatar-img"
            />
            <div v-else class="header-avatar-placeholder">
              {{ userInitials }}
            </div>
          </div>
          <div class="header-info">
            <div class="header-name">{{ userFullName }}</div>
            <div class="header-email">{{ userEmail }}</div>
          </div>
        </div>

        <!-- Menu Items -->
        <div class="dropdown-divider"></div>

        <router-link
          to="/settings"
          class="dropdown-item"
          @click="closeDropdown"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
          </svg>
          <span>User Settings</span>
        </router-link>

        <div class="dropdown-divider"></div>

        <button class="dropdown-item logout-item" @click="handleLogout">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
          </svg>
          <span>Logout</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from '@/composables/core/useStore';
import { useAuth } from '@/composables/core/useAuth';
import { UserService } from '@/services/UserService';

export default defineComponent({
  name: 'UserProfileDropdown',
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    // Composables
    const store = useStore();
    const { logout: authLogout } = useAuth();

    // Local reactive state
    const isOpen = ref(false);
    const profileImageUrl = ref<string | null>(null);
    const dropdown = ref<HTMLElement | null>(null);

    // Computed properties
    const userName = computed(() => store.getters['auth/userName'] || 'User');
    const userLastName = computed(() => store.getters['auth/userLastName'] || '');
    const userId = computed(() => store.getters['auth/userId']);
    const token = computed(() => store.getters['auth/token']);

    const userFullName = computed(() => {
      const fullName = `${userName.value} ${userLastName.value}`.trim();
      return fullName || 'User';
    });

    const userEmail = computed(() => store.getters['auth/userEmail'] || 'user@example.com');

    const userInitials = computed(() => {
      const firstInitial = userName.value.charAt(0).toUpperCase();
      const lastInitial = userLastName.value.charAt(0).toUpperCase();
      return firstInitial + (lastInitial || '');
    });

    const userProfileImage = computed(() => profileImageUrl.value);

    // Methods
    const toggleDropdown = (): void => {
      isOpen.value = !isOpen.value;
    };

    const closeDropdown = (): void => {
      isOpen.value = false;
    };

    const handleLogout = async (): Promise<void> => {
      closeDropdown();
      await authLogout();
      
      // Wait for next tick to ensure Vuex state has propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Force page reload to ensure clean state
      window.location.href = '/auth';
    };

    const handleClickOutside = (event: MouseEvent): void => {
      const dropdownEl = dropdown.value;
      if (dropdownEl && !dropdownEl.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    const loadProfileImage = async (): Promise<void> => {
      if (!userId.value) return;

      // First, try to load from localStorage for instant display
      const cachedImage = localStorage.getItem('userProfileImage');
      if (cachedImage) {
        profileImageUrl.value = cachedImage;
      }

      // Then fetch from backend
      try {
        const url = await UserService.getProfilePhoto(userId.value);

        if (url) {
          // Use the signed URL from Google Cloud Storage
          profileImageUrl.value = url;
          // Cache in localStorage
          localStorage.setItem('userProfileImage', url);
        }
      } catch (error: any) {
        // If 404 or no photo found, that's okay - user hasn't uploaded one yet
        if (error.response?.status !== 404) {
          console.error('Error loading profile image:', error);
        }
      }
    };

    // Lifecycle hooks
    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      loadProfileImage();
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      // Refs
      isOpen,
      dropdown,
      // Computed
      userName,
      userLastName,
      userFullName,
      userEmail,
      userInitials,
      userProfileImage,
      userId,
      token,
      // Methods
      toggleDropdown,
      closeDropdown,
      handleLogout
    };
  }
});
</script>

<style scoped>
/* ============================================
   Profile Dropdown Container
   ============================================ */
.profile-dropdown {
  position: relative;
}

/* ============================================
   Profile Button
   ============================================ */
.profile-button {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: 0.5rem 1rem;
  background: rgba(25, 135, 84, 0.08);
  border: 2px solid rgba(25, 135, 84, 0.3);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: var(--transition-base);
}

.profile-button:hover,
.profile-button.active {
  background: rgba(25, 135, 84, 0.15);
  border-color: var(--color-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(25, 135, 84, 0.2);
}

/* Collapsed State - Show only avatar */
.profile-button.collapsed {
  justify-content: center;
  padding: 0.5rem;
  gap: 0;
}

/* ============================================
   Avatar
   ============================================ */
.profile-avatar {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-circle);
  overflow: hidden;
  flex-shrink: 0;
  background: var(--color-bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
  color: var(--color-text-inverse);
  font-weight: var(--font-weight-bold);
  font-size: 0.875rem;
}

/* ============================================
   Profile Name
   ============================================ */
.profile-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

/* ============================================
   Dropdown Arrow
   ============================================ */
.dropdown-arrow {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
  color: var(--color-primary);
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

/* ============================================
   Dropdown Menu
   ============================================ */
.dropdown-menu {
  display: block !important;
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 0;
  min-width: 280px;
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1000;
  border: 1px solid #e9ecef;
}

/* ============================================
   Dropdown Header
   ============================================ */
.dropdown-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  padding: var(--spacing-4);
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
  color: var(--color-text-inverse);
}

.header-avatar {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-circle);
  overflow: hidden;
  flex-shrink: 0;
  background: var(--color-bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.header-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.3);
  color: var(--color-text-inverse);
  font-weight: var(--font-weight-bold);
  font-size: 1.25rem;
}

.header-info {
  flex: 1;
  min-width: 0;
}

.header-name {
  font-weight: var(--font-weight-bold);
  font-size: 1rem;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-email {
  font-size: 0.875rem;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ============================================
   Dropdown Items
   ============================================ */
.dropdown-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: 0.875rem 1rem;
  width: 100%;
  background: transparent;
  border: none;
  color: #495057;
  font-size: 0.95rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition-fast);
}

.dropdown-item:hover {
  background: #f8f9fa;
  color: var(--color-primary);
}

.dropdown-item svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.logout-item {
  color: #dc3545;
}

.logout-item:hover {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

/* ============================================
   Dropdown Divider
   ============================================ */
.dropdown-divider {
  height: 1px;
  background: #e9ecef;
  margin: 0;
}

/* ============================================
   Dropdown Animation
   ============================================ */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: var(--transition-fast);
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(10px) translateX(-10px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(10px) translateX(-10px);
}

/* ============================================
   Responsive Design
   ============================================ */
@media (max-width: 768px) {
  .profile-name {
    display: none;
  }

  .profile-button {
    padding: var(--spacing-2);
  }

  .dropdown-menu {
    min-width: 260px;
  }
}
</style>
